import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDailyPuzzle } from "../lib/api";
const storageKey = (date, difficulty) => `sudoku:daily:${date}:${difficulty}`;
export function useDaily(onReady) {
    const todayDate = new Date().toISOString().slice(0, 10);
    const [dailyDifficulty, setDailyDifficulty] = useState("medium");
    const [status, setStatus] = useState("idle");
    // Ref so we can call onReady from inside useEffect without stale closure
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const alreadyCompleted = (() => {
        try {
            return localStorage.getItem(storageKey(todayDate, dailyDifficulty)) === "1";
        }
        catch {
            return false;
        }
    })();
    const query = useQuery({
        queryKey: ["daily", todayDate, dailyDifficulty],
        queryFn: () => fetchDailyPuzzle(dailyDifficulty, todayDate),
        staleTime: 1000 * 60 * 60,
        enabled: false,
    });
    // When data arrives, set status to ready AND auto-call onReady so the
    // caller can start the game immediately without a second click.
    useEffect(() => {
        if (query.isFetching) {
            setStatus("loading");
            return;
        }
        if (query.isError) {
            setStatus("error");
            return;
        }
        if (query.data) {
            setStatus("ready");
            onReadyRef.current?.(query.data);
        }
    }, [query.isFetching, query.isError, query.data]);
    const loadDaily = useCallback((_difficulty) => {
        setStatus("loading");
        // If already cached by React Query, data is available synchronously
        // after refetch resolves — onReady fires via the useEffect above.
        query.refetch();
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.refetch]);
    const markPlaying = useCallback(() => setStatus("playing"), []);
    const markCompleted = useCallback(() => {
        try {
            localStorage.setItem(storageKey(todayDate, dailyDifficulty), "1");
        }
        catch { /* private browsing — not fatal */ }
        setStatus("completed");
    }, [todayDate, dailyDifficulty]);
    useEffect(() => {
        setStatus("idle");
    }, [dailyDifficulty]);
    return {
        todayDate,
        dailyPuzzle: query.data ?? null,
        status,
        alreadyCompleted,
        loadDaily,
        markPlaying,
        markCompleted,
        dailyDifficulty,
        setDailyDifficulty,
    };
}
